import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { ApiChannel } from 'core/constants';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { buildHeaders, XmStore } from 'core/services';
import { StoreAction } from 'store/actions';
import { ModelBase } from 'store/model-base';
import { CmsNowCore, CmsNowDeviceDetails, CmsNowLines, CmsNowModals, CmsNowNumberLock, CmsNowReturnExchange, CmsNowStepUpAuth } from './models';
import { CmsNowEsim } from './models/esim';
import { CmsNowMoreOptions } from './models/more-options';

@Injectable()
export class CmsNowApi {
    public static getCore(_xmStore: XmStore, http: HttpClient): Observable<CmsNowCore> {
        const requests: Observable<object>[] = [
            http.get('/now-mobile/core/modals.json', { headers: buildHeaders({ apiChannel: ApiChannel.CMS }) }),
            http.get('/now-mobile/core/meta-data.json', { headers: buildHeaders({ apiChannel: ApiChannel.CMS }) }),
            http.get('/now-mobile/core/nav.json', { headers: buildHeaders({ apiChannel: ApiChannel.CMS }) }),
            http.get('/now-mobile/core/icons.json', { headers: buildHeaders({ apiChannel: ApiChannel.CMS }) }),
            http.get('/now-mobile/core/common.json', { headers: buildHeaders({ apiChannel: ApiChannel.CMS }) })
        ];

        return forkJoin(requests).pipe(
            map((response: [SiteModals, SiteNowMetaData, SiteNav, CmsIcons, CmsCommon]) => {
                const [modals, metaData, nav, icons, common]:
                [SiteModals, SiteNowMetaData, SiteNav, CmsIcons, CmsCommon] = response;

                return CmsNowCore.create<CmsNowCore>({ modals, metaData, nav, icons, common });
            })
        );
    }

    public static getNowMobileModals(_xmStore: XmStore, http: HttpClient): Observable<CmsNowModals> {
        return http.get('/now-mobile/modals.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowModals) => CmsNowModals.create<CmsNowModals>({ modals: response }))
        );
    }

    public static getLinesPage(_xmStore: XmStore, http: HttpClient): Observable<CmsNowLines> {
        return http.get('/now-mobile/lines.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowLines) => CmsNowLines.create<CmsNowLines>(response))
        );
    }

    public static getDeviceDetailsPage(_xmStore: XmStore, http: HttpClient): Observable<CmsNowDeviceDetails> {
        return http.get('/now-mobile/devices.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowDeviceDetails) => CmsNowDeviceDetails.create<CmsNowDeviceDetails>(response))
        );
    }

    public static getNowMoreOptions(_xmStore: XmStore, http: HttpClient): Observable<CmsNowMoreOptions> {
        return http.get('/now-mobile/more-options.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowMoreOptions) => CmsNowMoreOptions.create<CmsNowMoreOptions>(response))
        );
    }

    public static getReturnExchangePage(_xmStore: XmStore, http: HttpClient): Observable<CmsNowReturnExchange> {
        return http.get('/now-mobile/return-exchange.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowReturnExchange) => CmsNowReturnExchange.create<CmsNowReturnExchange>(response))
        );
    }

    public static getEsimPage(_xmStore: XmStore, http: HttpClient): Observable<CmsNowEsim> {
        return http.get('/now-mobile/esim.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowEsim) => CmsNowReturnExchange.create<CmsNowEsim>(response))
        );
    }

    public static getNowNumberLock(_xmStore: XmStore, http: HttpClient): Observable<CmsNowNumberLock> {
        return http.get('/now-mobile/number-lock.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowNumberLock) => CmsNowNumberLock.create<CmsNowNumberLock>(response))
        );
    }

    public static getNowStepUpAuth(_xmStore: XmStore, http: HttpClient): Observable<CmsNowStepUpAuth> {
        return http.get('/now-mobile/step-up-auth.json', {
            headers: buildHeaders({ apiChannel: ApiChannel.CMS })
        }).pipe(
            map((response: CmsNowStepUpAuth) => CmsNowStepUpAuth.create<CmsNowStepUpAuth>(response))
        );
    }
}

ModelBase.fetchMapping[StoreAction.GET_NOW_CORE] = CmsNowApi.getCore;
ModelBase.fetchMapping[StoreAction.GET_NOW_LINES] = CmsNowApi.getLinesPage;
ModelBase.fetchMapping[StoreAction.GET_NOW_DEVICE_DETAILS] = CmsNowApi.getDeviceDetailsPage;
ModelBase.fetchMapping[StoreAction.GET_NOW_MODALS] = CmsNowApi.getNowMobileModals;
ModelBase.fetchMapping[StoreAction.GET_NOW_MORE_OPTIONS] = CmsNowApi.getNowMoreOptions;
ModelBase.fetchMapping[StoreAction.GET_NOW_NUMBER_LOCK] = CmsNowApi.getNowNumberLock;
ModelBase.fetchMapping[StoreAction.GET_NOW_STEP_UP_AUTH] = CmsNowApi.getNowStepUpAuth;
ModelBase.fetchMapping[StoreAction.GET_NOW_RETURN_EXCHANGE] = CmsNowApi.getReturnExchangePage;
ModelBase.fetchMapping[StoreAction.GET_NOW_ESIM] = CmsNowApi.getEsimPage;
